import React, { useState, useEffect, useMemo } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import ProductSelector from './ProductSelector';
import TimeRangeSelector from './TimeRangeSelector';
import Toggle from './Toggle';
import AssessmentBarChart from './AssessmentBarChart';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import { computeBayesianMonthlyNPS, NPS_CONFIG, aggregatePostsToMonthlyCounts } from '../functions/bayesianNPS';
import '../styles/NPSComparisonChart.css';
import { getAssessmentKeywords } from '../utils/assessmentKeywords';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const CHART_COLORS = {
    main: {
        line: 'rgb(47, 71, 184)',  // dark blue for main product
        fill: 'rgba(47, 71, 184, 0.1)'
    },
    competitors: [
        { line: 'rgb(75, 192, 192)', fill: 'rgba(75, 192, 192, 0.1)' },     // cyan blue
        { line: 'rgb(138, 43, 226)', fill: 'rgba(138, 43, 226, 0.1)' },    // Purple
        { line: 'rgb(134, 0, 46)', fill: 'rgba(134, 0, 46, 0.1)' },     // dark red
        { line: 'rgb(255, 99, 71)', fill: 'rgba(255, 99, 71, 0.1)' },     // Tomato red
        { line: 'rgb(50, 205, 50)', fill: 'rgba(50, 205, 50, 0.1)' },     // Lime green
        { line: 'rgb(255, 140, 0)', fill: 'rgba(255, 140, 0, 0.1)' },     // Dark orange
    ]
};

const NPSComparisonChart = ({ topicMap, stat_name = 'NPS' }) => {
    const [selectedProduct, setSelectedProduct] = useState("");
    const [timeRange, setTimeRange] = useState('3y');
    const [chartData, setChartData] = useState(null);
    const [summaryStats, setSummaryStats] = useState({});
    const [showRawScores, setShowRawScores] = useState(false);
    const [comparisonData, setComparisonData] = useState(null);
    const [filteredPosts, setFilteredPosts] = useState(null);
    const [filterTitle, setFilterTitle] = useState("");

    // Filter topicMap to only include topics with NPS data
    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};
        
        const filtered = {};
        Object.keys(topicMap).forEach(id => {
            const topic = topicMap[id];
            if (topic?.assessments?.NPS && 
                Array.isArray(topic.assessments.NPS) && 
                topic.assessments.NPS.length > 0) {
                filtered[id] = topic;
            }
        });
        
        console.log('Filtered topic map:', {
            original: Object.keys(topicMap).length,
            filtered: Object.keys(filtered).length,
            topics: Object.values(filtered).map(t => t.name)
        });
        
        return filtered;
    }, [topicMap]);

    // Set initial product selection
    useEffect(() => {
        if (filteredTopicMap && Object.keys(filteredTopicMap).length > 0 && !selectedProduct) {
            // Sort by priority and select the first one
            const sortedTopics = Object.values(filteredTopicMap)
                .sort((a, b) => {
                    const priorityA = a.priority === undefined ? Infinity : a.priority;
                    const priorityB = b.priority === undefined ? Infinity : b.priority;
                    return priorityA - priorityB;
                });
            
            if (sortedTopics[0]) {
                console.log('Setting initial product selection:', sortedTopics[0].id);
                setSelectedProduct(sortedTopics[0].id);
            }
        }
    }, [filteredTopicMap, selectedProduct]);

    // Debug logging
    useEffect(() => {
        console.log('NPSComparisonChart - Current state:', {
            topicMap: filteredTopicMap ? Object.keys(filteredTopicMap).length : 0,
            selectedProduct,
            hasChartData: !!chartData,
            selectedTopicData: selectedProduct ? filteredTopicMap?.[selectedProduct] : null
        });
    }, [filteredTopicMap, selectedProduct, chartData]);

    const calculateDensity = (npsData) => {
        if (!Array.isArray(npsData)) return { density: new Array(11).fill(0), mean: 0, count: 0, histogram: new Array(11).fill(0) };

        const histogram = new Array(11).fill(0); // 0-10 ratings
        let total = 0;
        let count = 0;

        npsData.forEach(post => {
            if (post?.rating !== null && post?.rating !== undefined) {
                histogram[Math.round(post.rating)]++;
                total += post.rating;
                count++;
            }
        });

        // Convert to percentages
        const density = histogram.map(value => count > 0 ? (value / count) * 100 : 0);

        return {
            density,
            mean: count > 0 ? total / count : 0,
            count,
            histogram
        };
    };

    const calculateNPSBreakdown = (npsData) => {
        if (!Array.isArray(npsData)) return { promoters: 0, detractors: 0, neutrals: 0, total: 0 };

        let promoters = 0;
        let detractors = 0;
        let neutrals = 0;
        let total = 0;

        npsData.forEach(post => {
            if (post?.rating !== null && post?.rating !== undefined) {
                const rating = post.rating;
                if (rating > NPS_CONFIG.PROMOTER_THRESHOLD) promoters++;
                else if (rating <= NPS_CONFIG.DETRACTOR_THRESHOLD) detractors++;
                else neutrals++;
                total++;
            }
        });

        return {
            promoters: total > 0 ? (promoters / total) * 100 : 0,
            detractors: total > 0 ? (detractors / total) * 100 : 0,
            neutrals: total > 0 ? (neutrals / total) * 100 : 0,
            total
        };
    };

    const getStartDateFromTimeRange = (range) => {
        const now = new Date();
        const years = parseInt(range);
        if (isNaN(years)) return null;
        return new Date(now.setFullYear(now.getFullYear() - years));
    };

    const filterPostsByDate = (npsData, startDate) => {
        if (!startDate || !Array.isArray(npsData)) return npsData;
        return npsData.filter(post => {
            const postDate = new Date(post.time * 1000);
            return postDate >= startDate;
        });
    };

    const calculateBayesianNPS = (npsData) => {
        if (!Array.isArray(npsData)) return 0;

        // Convert posts to monthly data format expected by computeBayesianMonthlyNPS
        const monthlyData = [{
            month: '2024-01', // We only need one month since we want overall NPS
            counts: new Array(11).fill(0)
        }];

        npsData.forEach(post => {
            if (post?.rating !== null && post?.rating !== undefined) {
                monthlyData[0].counts[Math.round(post.rating)]++;
            }
        });

        const results = computeBayesianMonthlyNPS(
            monthlyData,
            NPS_CONFIG.DEFAULT_PRIOR_ALPHA.map(alpha => alpha * NPS_CONFIG.DEFAULT_PRIOR_ALPHA_SCALAR),
            NPS_CONFIG.DEFAULT_DISCOUNT_FACTOR,
            NPS_CONFIG.DEFAULT_SAMPLES
        );

        return results[0]?.meanNPS || 0;
    };

    useEffect(() => {
        if (!filteredTopicMap || !selectedProduct) return;

        const selectedTopic = filteredTopicMap[selectedProduct];
        if (!selectedTopic) return;

        console.log('Processing data for topic:', {
            name: selectedTopic.name,
            npsDataLength: selectedTopic.assessments.NPS?.length || 0,
            hasCompetitors: selectedTopic.competitors?.length > 0,
            competitors: selectedTopic.competitors?.map(c => ({
                name: c.topic,
                hasNPS: !!c.assessments?.NPS,
                npsLength: c.assessments?.NPS?.length || 0
            }))
        });

        const startDate = getStartDateFromTimeRange(timeRange);

        // Process main product data
        const mainNPSData = filterPostsByDate(selectedTopic.assessments.NPS, startDate);
        const mainStats = calculateDensity(mainNPSData);
        const mainNPSBreakdown = calculateNPSBreakdown(mainNPSData);
        const mainBayesianNPS = calculateBayesianNPS(mainNPSData);

        console.log('Main product stats:', {
            filteredDataLength: mainNPSData?.length,
            density: mainStats.density,
            breakdown: mainNPSBreakdown,
            bayesianNPS: mainBayesianNPS
        });

        // Process competitor data
        const competitorStats = {};
        const competitorBreakdowns = {};
        const competitorBayesianNPS = {};

        if (selectedTopic.competitors) {
            selectedTopic.competitors.forEach(competitor => {
                if (!competitor?.assessments?.NPS) return;
                const competitorNPSData = filterPostsByDate(competitor.assessments.NPS, startDate);
                competitorStats[competitor.topic] = calculateDensity(competitorNPSData);
                competitorBreakdowns[competitor.topic] = calculateNPSBreakdown(competitorNPSData);
                competitorBayesianNPS[competitor.topic] = calculateBayesianNPS(competitorNPSData);

                console.log(`Competitor stats for ${competitor.topic}:`, {
                    filteredDataLength: competitorNPSData?.length,
                    density: competitorStats[competitor.topic].density,
                    breakdown: competitorBreakdowns[competitor.topic],
                    bayesianNPS: competitorBayesianNPS[competitor.topic]
                });
            });
        }

        // Prepare density chart data
        const datasets = [
            {
                label: selectedTopic.name,
                data: mainStats.density,
                backgroundColor: CHART_COLORS.main.fill,
                borderColor: CHART_COLORS.main.line,
                borderWidth: 2,
                tension: 0.4,
                fill: true,
                order: 1,
                pointRadius: 0,
                sampleSize: mainStats.count
            }
        ];

        Object.entries(competitorStats).forEach(([name, stats], index) => {
            const colorSet = CHART_COLORS.competitors[index % CHART_COLORS.competitors.length];
            datasets.push({
                label: name,
                data: stats.density,
                backgroundColor: colorSet.fill,
                borderColor: colorSet.line,
                borderWidth: 2,
                tension: 0.4,
                fill: true,
                order: index + 2,
                pointRadius: 0,
                sampleSize: stats.count
            });
        });

        setChartData({
            labels: Array.from({ length: 11 }, (_, i) => i.toString()),
            datasets
        });

        // Prepare comparison data
        const allCompanies = [
            {
                name: selectedTopic.name,
                stats: mainStats,
                breakdown: mainNPSBreakdown,
                bayesianNPS: mainBayesianNPS,
                isMain: true
            },
            ...Object.entries(competitorStats).map(([name, stats]) => ({
                name,
                stats,
                breakdown: competitorBreakdowns[name],
                bayesianNPS: competitorBayesianNPS[name],
                isMain: false
            }))
        ];

        // Sort by Bayesian NPS or raw score
        allCompanies.sort((a, b) => b.bayesianNPS - a.bayesianNPS);

        setComparisonData(allCompanies);

        // Update summary stats
        setSummaryStats({
            main: {
                name: selectedTopic.name,
                ...mainStats,
                breakdown: mainNPSBreakdown,
                bayesianNPS: mainBayesianNPS
            },
            competitors: Object.entries(competitorStats).map(([name, stats]) => ({
                name,
                ...stats,
                breakdown: competitorBreakdowns[name],
                bayesianNPS: competitorBayesianNPS[name]
            }))
        });

    }, [filteredTopicMap, selectedProduct, timeRange, stat_name]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Percentage of Reviews',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                ticks: {
                    callback: function(value) {
                        return value + '%';
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',  // Lighter grid lines
                    drawBorder: false
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'NPS Rating',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',  // Lighter grid lines
                    drawBorder: false
                }
            }
        },
        plugins: {
            legend: {
                position: 'top',
                align: 'end'
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.dataset.label || '';
                        const value = context.parsed.y.toFixed(1);
                        const sampleSize = context.dataset.sampleSize;
                        return [
                            `${label}: ${value}%`,
                            `Sample Size: ${sampleSize}`
                        ];
                    }
                }
            }
        }
    };

    const handleBarClick = (company) => {
        const selectedTopic = filteredTopicMap[selectedProduct];
        const assessments = company.isMain 
            ? selectedTopic?.assessments?.NPS || []
            : selectedTopic?.competitors?.find(c => c.topic === company.name)?.assessments?.NPS || [];
            
        // Apply time filter to the posts
        const startDate = getStartDateFromTimeRange(timeRange);
        const timeFilteredPosts = filterPostsByDate(assessments, startDate);
        
        setFilterTitle(`All NPS Reviews for ${company.name}`);
        setFilteredPosts(timeFilteredPosts);

        // Scroll to the filtered posts
        setTimeout(() => {
            const element = document.getElementById('filtered-nps-posts');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const comparisonChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: (context) => {
                        if (context.tick.value === 0) {
                            return '#d1d5db';
                        }
                        return '#e5e7eb';
                    },
                    lineWidth: (context) => {
                        if (context.tick.value === 0) {
                            return 1.5;
                        }
                        return 1;
                    },
                    drawBorder: false
                },
                border: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12,
                        family: "'Inter', sans-serif"
                    },
                    color: '#666',
                    padding: 8
                },
                title: {
                    display: true,
                    text: showRawScores ? 'Average Rating' : 'Net Promoter Score',
                    font: {
                        size: 13,
                        family: "'Inter', sans-serif",
                        weight: '500'
                    },
                    color: '#374151',
                    padding: { bottom: 16 }
                },
                afterFit: (scaleInstance) => {
                    scaleInstance.width = 60;
                }
            },
            x: {
                grid: {
                    display: false
                },
                border: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12,
                        family: "'Inter', sans-serif"
                    },
                    color: '#666',
                    padding: 8
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                titleFont: {
                    size: 14,
                    family: "'Inter', sans-serif",
                    weight: '600'
                },
                bodyFont: {
                    size: 13,
                    family: "'Inter', sans-serif"
                },
                padding: 12,
                cornerRadius: 6,
                callbacks: {
                    title: function(context) {
                        return context[0].label;
                    },
                    label: function(context) {
                        const company = comparisonData[context.dataIndex];
                        const value = context.parsed.y.toFixed(1);
                        return [
                            `${showRawScores ? 'Average Rating' : 'NPS'}: ${value}`,
                            `Sample Size: ${company.stats.count}`
                        ];
                    }
                }
            }
        },
        onClick: (event, elements) => {
            if (elements.length > 0) {
                const index = elements[0].index;
                handleBarClick(comparisonData[index]);
            }
        }
    };

    const comparisonChartData = {
        labels: comparisonData?.map(c => c.name) || [],
        datasets: [{
            data: comparisonData?.map(c => showRawScores ? c.stats.mean : c.bayesianNPS) || [],
            backgroundColor: comparisonData?.map(c => 
                c.isMain ? '#1a56db' : '#4fd1c5'
            ) || [],
            borderWidth: 0,
            borderRadius: 6,
            barThickness: 120,
            maxBarThickness: 200
        }]
    };

    const handleTimeRangeChange = (newRange) => {
        setTimeRange(newRange);
    };

    const handleSegmentClick = (posts, segmentType, companyName) => {
        // Apply time filter to the posts
        const startDate = getStartDateFromTimeRange(timeRange);
        const timeFilteredPosts = filterPostsByDate(posts, startDate);
        
        // Format the title based on the segment type and company
        let title;
        if (segmentType === 'positive') {
            title = `${timeFilteredPosts.length} Promoters${companyName ? ` for ${companyName}` : ''}`;
        } else if (segmentType === 'negative') {
            title = `${timeFilteredPosts.length} Detractors${companyName ? ` for ${companyName}` : ''}`;
        } else {
            title = `${timeFilteredPosts.length} Passives${companyName ? ` for ${companyName}` : ''}`;
        }

        setFilterTitle(title);
        setFilteredPosts(timeFilteredPosts);

        // Scroll to the filtered posts
        setTimeout(() => {
            const element = document.getElementById('filtered-nps-posts');
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const clearFilteredPosts = () => {
        setFilteredPosts(null);
        setFilterTitle("");
    };

    return (
        <div className="nps-comparison-chart">
            <div className="chart-controls">
                <ProductSelector
                    topicMap={filteredTopicMap}
                    selectedId={selectedProduct}
                    onSelect={setSelectedProduct}
                />
                <TimeRangeSelector
                    timeRange={timeRange}
                    onTimeRangeChange={handleTimeRangeChange}
                />
            </div>

            {chartData && (
                <>
                    <div className="chart-wrapper">
                        <Line data={chartData} options={chartOptions} />
                    </div>
                    
                    <div className="comparison-section">
                        <div className="section-header">
                            <h3>NPS vs. Peers</h3>
                            <Toggle
                                label="Show Raw Scores"
                                checked={showRawScores}
                                onChange={setShowRawScores}
                            />
                        </div>
                        <div className="comparison-chart-wrapper">
                            <Bar data={comparisonChartData} options={comparisonChartOptions} />
                        </div>
                        
                        <div className="nps-breakdown-grid">
                            {comparisonData?.map((company) => {
                                const selectedTopic = filteredTopicMap[selectedProduct];
                                return (
                                    <div 
                                        key={company.name} 
                                        className={`nps-breakdown-card ${company.isMain ? 'main-product' : 'competitor'}`}
                                    >
                                        <div className="breakdown-row">
                                            <div className="bar-section">
                                                <h4>{company.name}</h4>
                                                <AssessmentBarChart
                                                    assessments={company.isMain 
                                                        ? selectedTopic?.assessments?.NPS || []
                                                        : selectedTopic?.competitors?.find(c => c.topic === company.name)?.assessments?.NPS || []
                                                    }
                                                    thresholds={{
                                                        POSITIVE: NPS_CONFIG.PROMOTER_THRESHOLD,
                                                        NEGATIVE: NPS_CONFIG.DETRACTOR_THRESHOLD
                                                    }}
                                                    onSegmentClick={(posts, segmentType) => {
                                                        handleSegmentClick(posts, segmentType, company.name);
                                                    }}
                                                    labels={{
                                                        segments: {
                                                            positive: 'Promoters',
                                                            neutral: 'Passives',
                                                            negative: 'Detractors'
                                                        }
                                                    }}
                                                />
                                                <div className="sample-size" style={{ textAlign: 'left', fontSize: '12px' }}>
                                                    Sample Size: {company.stats.count} reviews
                                                </div>
                                            </div>
                                            
                                            <div className="stats-section">
                                                <div className="metric-container">
                                                    <div className="metric-label">Bayesian NPS</div>
                                                    <div className="metric-value">{company.bayesianNPS.toFixed(0)}</div>
                                                </div>
                                                
                                                <div className="metric-container">
                                                    <div className="metric-label">Raw Score</div>
                                                    <div className="metric-value" style={{ fontSize: '24px' }}>{company.stats.mean.toFixed(1)} / 10</div>
                                                </div>
                                                
                                  
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {filteredPosts && (
                            <div id="filtered-nps-posts" className="filtered-posts-container">
                                <FilteredPostsDisplay
                                    posts={filteredPosts}
                                    title={filterTitle}
                                    onClose={clearFilteredPosts}
                                    showBookmarkButton={true}
                                    ratingDisplayName="NPS Rating"
                                    ratingColorScheme={{
                                        high: NPS_CONFIG.PROMOTER_THRESHOLD,
                                        low: NPS_CONFIG.DETRACTOR_THRESHOLD
                                    }}
                                    showCloseButton={true}
                                    query={getAssessmentKeywords('nps')}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default NPSComparisonChart; 